// service.ts
import type { CancelTokenSource } from "axios";
import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  GraphDataPayload,
  GraphDataResponse} from "../types/powerTypes";
import {
  GraphData
} from "../types/powerTypes";
import { axiosInstance } from "../../utils/common";

// Types for monthly data responses
interface MonthlyKwhResponse {
  month_start: string;
  current_datetime: string;
  first_reading: {
    value: number;
    datetime: string | null;
  };
  last_reading: {
    value: number;
    datetime: string | null;
  };
  total_kwh_generated: number;
}

interface MonthlyEngineHoursResponse {
  month_start: string;
  current_datetime: string;
  first_reading: {
    value: number;
    datetime: string | null;
  };
  last_reading: {
    value: number;
    datetime: string | null;
  };
  total_engine_hours: number;
}

// Cancel tokens
let cancelTokenSource4: CancelTokenSource | null = null;
let cancelTokenSourceMonthlyKwh: CancelTokenSource | null = null;
let cancelTokenSourceMonthlyEngineHours: CancelTokenSource | null = null;

// Utility to find site ID
const findSiteByName = (sites: any[], name: string) =>
  sites.find((site) => site.name === name);


// ─────────── Fetch and Filter Graph Data ───────────
export const fetchAndFilterGraphData = createAsyncThunk<
  GraphDataResponse,
  GraphDataPayload,
  { rejectValue: string }
>(
  "sites/fetchAndFilterGraphData",
  async (
    {
      selectedCustomerName,
      avail_sites,
      selectedSite,
      fromDate,
      toDate,
      bpValue,
      selectedGeneratorId,
      sourceId,
    },
    thunkAPI
  ) => {
    if (cancelTokenSource4) cancelTokenSource4.cancel("Request canceled");
    cancelTokenSource4 = axios.CancelToken.source();

    const site = findSiteByName(avail_sites, selectedSite);
    if (!site) return thunkAPI.rejectWithValue("Site not found");

    const now = new Date();
    const defaultFrom = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).toISOString();
    const defaultTo = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      23,
      59,
      59,
      999
    ).toISOString();

    const postData = {
      fromrange: fromDate ?? defaultFrom,
      torange: toDate ?? defaultTo,
      value_name: bpValue,
      source_id: sourceId,
    };

    try {
      const { data } = await axiosInstance.post<GraphDataResponse>(
        `trend/graphdata/${site.id}/${selectedGeneratorId}/`,
        postData,
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSource4.token,
        }
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// ─────────── Fetch Monthly kWh ───────────
export const fetchMonthlyKwh = createAsyncThunk<
  MonthlyKwhResponse,
  {
    selectedCustomerName: string;
    avail_sites: any[];
    selectedSite: string;
    selectedGeneratorId: number;
  },
  { rejectValue: string }
>(
  "sites/fetchMonthlyKwh",
  async (
    { selectedCustomerName, avail_sites, selectedSite, selectedGeneratorId },
    thunkAPI
  ) => {
    if (cancelTokenSourceMonthlyKwh) cancelTokenSourceMonthlyKwh.cancel("Request canceled");
    cancelTokenSourceMonthlyKwh = axios.CancelToken.source();

    const site = findSiteByName(avail_sites, selectedSite);
    if (!site) return thunkAPI.rejectWithValue("Site not found");

    try {
      const { data } = await axiosInstance.post<MonthlyKwhResponse>(
        `trend/monthly_kwh/${site.id}/${selectedGeneratorId}/`,
        { source_id: 'bluepillar' },
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSourceMonthlyKwh.token,
        }
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// ─────────── Fetch Monthly Engine Hours ───────────
export const fetchMonthlyEngineHours = createAsyncThunk<
  MonthlyEngineHoursResponse,
  {
    selectedCustomerName: string;
    avail_sites: any[];
    selectedSite: string;
    selectedGeneratorId: number;
  },
  { rejectValue: string }
>(
  "sites/fetchMonthlyEngineHours",
  async (
    { selectedCustomerName, avail_sites, selectedSite, selectedGeneratorId },
    thunkAPI
  ) => {
    if (cancelTokenSourceMonthlyEngineHours) cancelTokenSourceMonthlyEngineHours.cancel("Request canceled");
    cancelTokenSourceMonthlyEngineHours = axios.CancelToken.source();

    const site = findSiteByName(avail_sites, selectedSite);
    if (!site) return thunkAPI.rejectWithValue("Site not found");

    try {
      const { data } = await axiosInstance.post<MonthlyEngineHoursResponse>(
        `trend/monthly_engine_hours/${site.id}/${selectedGeneratorId}/`,
        { source_id: 'bluepillar' },
        {
          headers: { customer: selectedCustomerName },
          cancelToken: cancelTokenSourceMonthlyEngineHours.token,
        }
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Export types for use in other files
export type { MonthlyKwhResponse, MonthlyEngineHoursResponse };
